import type { CapabilityId as SpecCapabilityId } from "@acme/mdx/component-spec";
import type { Fact } from "~/components/inside/facts";
import type { SheetArt } from "~/lib/constants/sheet-art";
import * as m from "~/paraglide/messages.js";

/**
 * The five kinds of system Lindevo builds: save time, make money, agent
 * teams, research and full systems. Kinds of system rather than products, so
 * each one names what the owner gets, not the technology inside it.
 *
 * The ids are the section anchors. Both the home page's overview and every
 * pain-point card link straight into them, so they are load-bearing.
 */

/** The section anchors; they live in the component spec, which maps the blog's `service-cta` ids onto them. */
export type CapabilityId = SpecCapabilityId;

export interface Capability {
  /** The artifact shown beside the copy. */
  art: SheetArt;
  /** The caption under the artifact saying it is illustrative. */
  artNote: string;
  body: string;
  id: CapabilityId;
  label: string;
  /** Where to read on: the matching example on the pain-points page. */
  link: { hash?: string; label: string; to: string };
  points: string[];
  /** Two lines; the design breaks each heading deliberately. */
  title: [string, string];
}

export function getCapabilities(): Capability[] {
  return [
    {
      art: {
        body: m.services_svc_save_art_body(),
        foot: m.services_svc_save_art_foot(),
        heading: m.services_svc_save_art_heading(),
        kind: "sample-document",
        source: { icon: "googledocs", label: m.services_svc_save_art_source() },
      },
      artNote: m.services_svc_save_art_note(),
      body: m.services_svc_save_body(),
      id: "save-time",
      label: m.services_svc_save_label(),
      link: {
        hash: "customer-questions",
        label: m.services_svc_save_link(),
        to: "/pain-points",
      },
      points: [
        m.services_svc_save_point_1(),
        m.services_svc_save_point_2(),
        m.services_svc_save_point_3(),
        m.services_svc_save_point_4(),
      ],
      title: [m.services_svc_save_title_1(), m.services_svc_save_title_2()],
    },
    {
      art: {
        kind: "routing",
        steps: [
          {
            detail: m.services_svc_money_art_1_detail(),
            icon: "hubspot",
            label: m.services_svc_money_art_1_label(),
            state: m.services_svc_money_art_1_state(),
          },
          {
            detail: m.services_svc_money_art_2_detail(),
            icon: "googledocs",
            label: m.services_svc_money_art_2_label(),
            state: m.services_svc_money_art_2_state(),
          },
          {
            detail: m.services_svc_money_art_3_detail(),
            icon: "gmail",
            label: m.services_svc_money_art_3_label(),
            state: m.services_svc_money_art_3_state(),
          },
        ],
      },
      artNote: m.services_svc_money_art_note(),
      body: m.services_svc_money_body(),
      id: "make-money",
      label: m.services_svc_money_label(),
      link: { label: m.services_svc_money_link(), to: "/projects/lead-radar" },
      points: [
        m.services_svc_money_point_1(),
        m.services_svc_money_point_2(),
        m.services_svc_money_point_3(),
      ],
      title: [m.services_svc_money_title_1(), m.services_svc_money_title_2()],
    },
    {
      art: {
        kind: "routing",
        steps: [
          {
            detail: m.services_svc_agents_art_1_detail(),
            icon: "googledrive",
            label: m.services_svc_agents_art_1_label(),
            state: m.services_svc_agents_art_1_state(),
          },
          {
            detail: m.services_svc_agents_art_2_detail(),
            icon: "googledocs",
            label: m.services_svc_agents_art_2_label(),
            state: m.services_svc_agents_art_2_state(),
          },
          {
            detail: m.services_svc_agents_art_3_detail(),
            icon: "slack",
            label: m.services_svc_agents_art_3_label(),
            state: m.services_svc_agents_art_3_state(),
          },
        ],
      },
      artNote: m.services_svc_agents_art_note(),
      body: m.services_svc_agents_body(),
      id: "agent-teams",
      label: m.services_svc_agents_label(),
      link: {
        label: m.services_svc_agents_link(),
        to: "/projects/ai-agent-team",
      },
      points: [
        m.services_svc_agents_point_1(),
        m.services_svc_agents_point_2(),
        m.services_svc_agents_point_3(),
      ],
      title: [m.services_svc_agents_title_1(), m.services_svc_agents_title_2()],
    },
    {
      art: {
        body: m.services_svc_research_art_body(),
        foot: m.services_svc_research_art_foot(),
        heading: m.services_svc_research_art_heading(),
        kind: "sample-document",
        source: {
          icon: "googledocs",
          label: m.services_svc_research_art_source(),
        },
      },
      artNote: m.services_svc_research_art_note(),
      body: m.services_svc_research_body(),
      id: "research",
      label: m.services_svc_research_label(),
      link: {
        label: m.services_svc_research_link(),
        to: "/projects/blog-engine",
      },
      points: [
        m.services_svc_research_point_1(),
        m.services_svc_research_point_2(),
        m.services_svc_research_point_3(),
      ],
      title: [
        m.services_svc_research_title_1(),
        m.services_svc_research_title_2(),
      ],
    },
    {
      art: {
        bar: {
          mark: true,
          marker: m.services_flow_marker(),
          title: m.services_svc_full_art_bar(),
        },
        kind: "queue",
        // One of the four waits on a person. That is the point of the queue.
        rows: [
          {
            label: m.services_svc_full_art_row_1(),
            state: m.services_svc_full_art_state_1(),
          },
          {
            flagged: true,
            label: m.services_svc_full_art_row_2(),
            state: m.services_svc_full_art_state_2(),
          },
          {
            label: m.services_svc_full_art_row_3(),
            state: m.services_svc_full_art_state_3(),
          },
          {
            label: m.services_svc_full_art_row_4(),
            state: m.services_svc_full_art_state_4(),
          },
        ],
      },
      artNote: m.services_svc_full_art_note(),
      body: m.services_svc_full_body(),
      id: "full-systems",
      label: m.services_svc_full_label(),
      link: {
        label: m.services_svc_full_link(),
        to: "/projects/ai-infrastructure",
      },
      points: [
        m.services_svc_full_point_1(),
        m.services_svc_full_point_2(),
        m.services_svc_full_point_3(),
        m.services_svc_full_point_4(),
      ],
      title: [m.services_svc_full_title_1(), m.services_svc_full_title_2()],
    },
  ];
}

/** What every engagement comes with, whichever capability it builds. */
export function getDeliveryList(): Fact[] {
  return [
    {
      term: m.services_delivery_1_term(),
      value: m.services_delivery_1_value(),
    },
    {
      term: m.services_delivery_2_term(),
      value: m.services_delivery_2_value(),
    },
    {
      term: m.services_delivery_3_term(),
      value: m.services_delivery_3_value(),
    },
  ];
}

/**
 * The five service slugs the site sold before the repositioning, mapped to the
 * capability that now covers them. `null` means nothing here corresponds, so
 * the redirect lands on the page itself rather than a section that would not
 * answer what the visitor came for.
 *
 * Kept so the old URLs keep working: they were indexed and linked.
 */
export const LEGACY_SERVICE_REDIRECTS: Record<string, CapabilityId | null> = {
  "booking-platform-development": "full-systems",
  "desktop-application-development": "full-systems",
  "process-optimization": "save-time",
  "seo-optimization": null,
  "social-marketing": null,
};
